import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldIssue, SalesCycle, SalesCycleImpactRequest } from '@complan/contracts';
import { usePageChrome } from '@/components/layout/page-chrome';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiRequestError } from '@/shared/api/api-error';
import { formatDateRange } from '@/shared/format/date-format';
import {
  useApplySalesCycle,
  useSalesCycleDefinitions,
  useSalesCycleImpact,
  useSaveSalesCycle,
} from './sales-cycle-data';
import {
  classifyWriteError,
  defaultReassignSelection,
  emptyFormValues,
  toWriteRequest,
  type SalesCycleFormValues,
  type WriteErrorClassification,
} from './sales-cycle-model';
import { SalesCycleForm } from './SalesCycleForm';
import { SalesCycleImpactList } from './SalesCycleImpactList';

/**
 * The Sales_Cycle_Timing_Form route (`/sales-cycle-timing`), task 23.3.
 *
 * This screen owns the flow the two-call date-change protocol implies (R6.7–R6.9)
 * and the R6.2/R6.3 error handling. It has two phases:
 *
 * - **definition** — the {@link SalesCycleForm} captures the year, name, dates and
 *   active state (R6.1). A create, or an edit that moves no date, saves directly
 *   through `POST`/`PUT`; a rejected save is classified so a hard block retains
 *   the entered values (R6.2, R6.3) and a warning is acknowledged and resubmitted
 *   (R6.4, R6.5).
 * - **impact** — when the user edits the dates of an *existing* cycle, saving
 *   first previews the affected Initiatives (R6.7); the {@link SalesCycleImpactList}
 *   shows the count and per-Initiative sets with per-Initiative selection, and
 *   confirming applies the change and reassigns only the selected Initiatives
 *   (R6.8, R6.9).
 *
 * The layout follows §1.4's `grid2` paired-card pattern (derived; flagged in the
 * report): the form card beside the "sales cycles this year" list card.
 */
export function SalesCycleTimingPage(): React.JSX.Element {
  const { t } = useTranslation();

  // Foundation ships no dedicated year selector on this screen; the planning year
  // is the form's own `planningYear` field, and the list mirrors it.
  const [values, setValues] = useState<SalesCycleFormValues>(() =>
    emptyFormValues(new Date().getFullYear()),
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [originalDates, setOriginalDates] = useState<{ start: string; end: string } | null>(null);
  const [classification, setClassification] = useState<WriteErrorClassification | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [phase, setPhase] = useState<'definition' | 'impact'>('definition');
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [impactError, setImpactError] = useState<string | null>(null);

  usePageChrome({
    title: t('salesCycle.page.title'),
    subtitle: t('salesCycle.page.subtitle', { year: values.planningYear }),
  });

  const definitionsQuery = useSalesCycleDefinitions(values.planningYear);
  const saveMutation = useSaveSalesCycle(values.planningYear);
  const impactMutation = useSalesCycleImpact();
  const applyMutation = useApplySalesCycle(values.planningYear);

  const definitions = definitionsQuery.data ?? [];

  // A date-only change to an existing cycle is what triggers the impact preview
  // rather than a direct save (R6.7).
  const isDateChange = useMemo(() => {
    if (editingId === null || originalDates === null) {
      return false;
    }
    return values.startDate !== originalDates.start || values.endDate !== originalDates.end;
  }, [editingId, originalDates, values.startDate, values.endDate]);

  const resetForm = (): void => {
    setValues(emptyFormValues(new Date().getFullYear()));
    setEditingId(null);
    setOriginalDates(null);
    setClassification(null);
    setAcknowledged(false);
    setPhase('definition');
    setSelectedIds(new Set());
    setImpactError(null);
  };

  const beginEdit = (cycle: SalesCycle): void => {
    setValues({
      planningYear: cycle.planningYear,
      name: cycle.name,
      startDate: cycle.startDate,
      endDate: cycle.endDate,
      isActive: cycle.isActive,
    });
    setEditingId(cycle.id);
    setOriginalDates({ start: cycle.startDate, end: cycle.endDate });
    setClassification(null);
    setAcknowledged(false);
    setSavedNotice(null);
    setPhase('definition');
    setImpactError(null);
  };

  const acknowledgedRuleIds =
    classification?.kind === 'acknowledgement_required'
      ? [...classification.acknowledgeableRuleIds]
      : [];

  const handleSubmit = (): void => {
    setSavedNotice(null);

    // Build and validate the request from the raw field state first: an empty or
    // malformed date is reported as a field issue here, and the entered values
    // are retained (R6.2, R6.3) because nothing resets the form on this path.
    const built = toWriteRequest(values, acknowledgedRuleIds);
    if (!built.ok) {
      setClassification(toBlockedFromFieldIssues(built.fieldIssues));
      return;
    }

    // Editing the dates of an existing cycle goes through the impact preview
    // before anything persists (R6.7).
    if (isDateChange && editingId !== null) {
      runImpactPreview(editingId, built.request.startDate, built.request.endDate);
      return;
    }

    saveMutation.mutate(
      {
        ...(editingId !== null ? { id: editingId } : {}),
        request: built.request,
      },
      {
        onSuccess: () => {
          setSavedNotice(t('salesCycle.form.saved'));
          resetForm();
          void definitionsQuery.refetch();
        },
        onError: (error) => {
          handleWriteError(error);
        },
      },
    );
  };

  const runImpactPreview = (
    cycleId: string,
    startDate: SalesCycleImpactRequest['startDate'],
    endDate: SalesCycleImpactRequest['endDate'],
  ): void => {
    setImpactError(null);
    impactMutation.mutate(
      {
        id: cycleId,
        request: { startDate, endDate, limit: 200, offset: 0 },
      },
      {
        onSuccess: (response) => {
          setSelectedIds(new Set(defaultReassignSelection(response.items)));
          setPhase('impact');
        },
        onError: (error) => {
          if (error instanceof ApiRequestError) {
            const outcome = classifyWriteError(error.body);
            // A blocked date range (R6.3) on the impact call still retains values.
            if (outcome.kind === 'blocked' || outcome.kind === 'acknowledgement_required') {
              setClassification(outcome);
              return;
            }
          }
          setImpactError(t('salesCycle.impact.loadError'));
        },
      },
    );
  };

  const handleWriteError = (error: unknown): void => {
    if (error instanceof ApiRequestError) {
      const outcome = classifyWriteError(error.body);
      setClassification(outcome);
      // A fresh warning invalidates any prior acknowledgement tick.
      if (outcome.kind === 'acknowledgement_required') {
        setAcknowledged(false);
      }
      return;
    }
    setClassification({ kind: 'other' });
  };

  const handleApply = (): void => {
    if (editingId === null) {
      return;
    }
    setImpactError(null);
    const built = toWriteRequest(values, acknowledgedRuleIds);
    if (!built.ok) {
      setClassification(toBlockedFromFieldIssues(built.fieldIssues));
      setPhase('definition');
      return;
    }
    applyMutation.mutate(
      {
        id: editingId,
        request: {
          ...built.request,
          reassignInitiativeIds: [...selectedIds],
        },
      },
      {
        onSuccess: (response) => {
          setSavedNotice(
            t('salesCycle.impact.applied', { count: response.reassignedInitiativeCount }),
          );
          resetForm();
          void definitionsQuery.refetch();
        },
        onError: (error) => {
          if (error instanceof ApiRequestError) {
            const outcome = classifyWriteError(error.body);
            if (outcome.kind === 'stale_preview') {
              setImpactError(t('salesCycle.impact.stalePreview'));
              setPhase('definition');
              return;
            }
            if (outcome.kind === 'blocked' || outcome.kind === 'acknowledgement_required') {
              setClassification(outcome);
              setPhase('definition');
              return;
            }
          }
          setImpactError(t('salesCycle.impact.loadError'));
        },
      },
    );
  };

  const toggleSelection = (initiativeId: string): void => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(initiativeId)) {
        next.delete(initiativeId);
      } else {
        next.add(initiativeId);
      }
      return next;
    });
  };

  const impactItems = impactMutation.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      {savedNotice !== null && (
        <Alert>
          <AlertTitle>{savedNotice}</AlertTitle>
        </Alert>
      )}
      {impactError !== null && (
        <Alert variant="destructive">
          <AlertTitle>{impactError}</AlertTitle>
        </Alert>
      )}

      {phase === 'definition' ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <SalesCycleForm
            values={values}
            onChange={(next) => {
              setValues(next);
              setClassification(null);
            }}
            onSubmit={handleSubmit}
            onReset={resetForm}
            isSubmitting={saveMutation.isPending || impactMutation.isPending}
            classification={classification}
            acknowledged={acknowledged}
            onAcknowledgedChange={setAcknowledged}
            isEditing={editingId !== null}
          />
          <ExistingCyclesCard
            cycles={definitions}
            isLoading={definitionsQuery.isLoading}
            isError={definitionsQuery.isError}
            editingId={editingId}
            onEdit={beginEdit}
          />
        </div>
      ) : (
        <SalesCycleImpactList
          items={impactItems}
          totalCount={impactMutation.data?.totalCount ?? 0}
          definitions={definitions}
          selectedIds={selectedIds}
          onToggle={toggleSelection}
          onSelectAllEligible={() =>
            setSelectedIds(new Set(defaultReassignSelection(impactItems)))
          }
          onClearSelection={() => setSelectedIds(new Set())}
          onApply={handleApply}
          onBack={() => setPhase('definition')}
          isApplying={applyMutation.isPending}
        />
      )}
    </div>
  );
}

/** The "sales cycles this year" list card (§3.5 table). */
function ExistingCyclesCard({
  cycles,
  isLoading,
  isError,
  editingId,
  onEdit,
}: {
  readonly cycles: readonly SalesCycle[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly editingId: string | null;
  readonly onEdit: (cycle: SalesCycle) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card className="gap-4 p-5">
      <p className="text-[13px] font-semibold text-txt">{t('salesCycle.existing.heading')}</p>
      {isError ? (
        <p className="text-[12.3px] text-destructive">{t('salesCycle.existing.loadError')}</p>
      ) : isLoading ? (
        <p className="text-muted-foreground text-[12.3px]">{t('common.loading')}</p>
      ) : cycles.length === 0 ? (
        <p className="text-muted-foreground text-[12.3px]">{t('salesCycle.existing.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[10.5px] uppercase">
                {t('salesCycle.existing.colName')}
              </TableHead>
              <TableHead className="text-[10.5px] uppercase">
                {t('salesCycle.existing.colRange')}
              </TableHead>
              <TableHead className="text-[10.5px] uppercase">
                {t('salesCycle.existing.colState')}
              </TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {cycles.map((cycle) => (
              <TableRow key={cycle.id} data-state={cycle.id === editingId ? 'selected' : undefined}>
                <TableCell className="text-[12px] font-medium text-txt">{cycle.name}</TableCell>
                <TableCell className="text-[11.5px]">
                  {formatDateRange(cycle.startDate, cycle.endDate)}
                </TableCell>
                <TableCell>
                  <Badge variant={cycle.isActive ? 'secondary' : 'outline'}>
                    {cycle.isActive
                      ? t('salesCycle.existing.active')
                      : t('salesCycle.existing.inactive')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button type="button" size="xs" variant="ghost" onClick={() => onEdit(cycle)}>
                    {cycle.id === editingId
                      ? t('salesCycle.existing.selected')
                      : t('salesCycle.existing.edit')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

/**
 * A client-side field-issue rejection expressed as a `blocked` classification, so
 * an empty or malformed date entered in the form is surfaced the same way a
 * server-side hard block is — highlighting the field and retaining the values.
 */
function toBlockedFromFieldIssues(fieldIssues: readonly FieldIssue[]): WriteErrorClassification {
  return {
    kind: 'blocked',
    findings: [],
    fields: [...new Set(fieldIssues.map((issue) => issue.path))],
  };
}
